import L from 'leaflet';
import type { ConnectorType } from '@/types';

/**
 * Pin colour per partner type. Labels are not duplicated here - use
 * CONNECTOR_TYPE_LABELS from @/types.
 */
export const PARTNER_TYPE_COLORS: Record<ConnectorType, string> = {
  broker: '#2563eb',
  builder: '#ea580c',
  channel_partner: '#16a34a',
  ca: '#9333ea',
  financial_advisor: '#0891b2',
  connector: '#ca8a04',
  dsa: '#db2777',
  hlc: '#4f46e5',
  banker: '#0f766e',
  others: '#64748b',
};

export const getPartnerTypeColor = (type: ConnectorType): string =>
  PARTNER_TYPE_COLORS[type] ?? PARTNER_TYPE_COLORS.others;

// Icons are pure markup, so they are built once per (type, favorite) pair and
// reused across every marker rather than re-created on each render.
const iconCache = new Map<string, L.DivIcon>();

/**
 * Build a map pin for a partner.
 *
 * Uses L.divIcon rather than the default Leaflet marker: the default pulls its
 * PNGs from a CDN, which fails offline and inside the native WebView. Inline
 * SVG has no network dependency and lets us colour-code by partner type.
 */
export function createPartnerIcon(
  type: ConnectorType,
  isFavorite: boolean
): L.DivIcon {
  const key = `${type}:${isFavorite}`;
  const cached = iconCache.get(key);
  if (cached) return cached;

  const color = getPartnerTypeColor(type);

  // Classic teardrop pin, 26x36, anchored at its tip.
  const star = isFavorite
    ? '<path d="M13 7.2l1.32 2.67 2.95.43-2.13 2.08.5 2.94L13 13.93l-2.64 1.39.5-2.94-2.13-2.08 2.95-.43z" fill="#fbbf24"/>'
    : '<circle cx="13" cy="11.5" r="4.2" fill="#ffffff" fill-opacity="0.92"/>';

  const html = `
    <svg width="26" height="36" viewBox="0 0 26 36" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 0.75C6.4 0.75 1.05 6.1 1.05 12.7c0 8.5 10.3 21.1 11.15 22.15a1.05 1.05 0 0 0 1.6 0C14.65 33.8 24.95 21.2 24.95 12.7 24.95 6.1 19.6 0.75 13 0.75z"
            fill="${color}" stroke="#ffffff" stroke-width="1.6"/>
      ${star}
    </svg>`;

  const icon = L.divIcon({
    html,
    className: 'partner-map-pin',
    iconSize: [26, 36],
    iconAnchor: [13, 36],
    popupAnchor: [0, -32],
  });

  iconCache.set(key, icon);
  return icon;
}
